import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type { RegistryFund, RegistryUnitPrice } from '../../api/registryDealTypes';
import { errorMessage } from '../../api/registryApi';
import { hasRegistryPermission, useRegistrySession } from '../../session/RegistrySession';
import { PageHeader, SectionCard, StatusChip } from '../../ui/primitives';

const empty = { effectiveDate: '', entry: '', mid: '', exit: '' };

function FundClasses({
  fund,
  prices,
  activeClass,
  select,
}: {
  fund: RegistryFund;
  prices: Record<string, RegistryUnitPrice[]>;
  activeClass: string;
  select: (id: string) => void;
}) {
  return (
    <>
      {fund.classes.map((unitClass) => (
        <SectionCard
          key={unitClass.externalId}
          title={`${unitClass.name} · ${unitClass.code}`}
          subtitle={`Issue price $${unitClass.issuePrice} · ${unitClass.openToApplications ? 'Open to applications' : 'Not open to applications'}`}
        >
          <Button size="small" onClick={() => select(unitClass.externalId)}>
            {activeClass === unitClass.externalId
              ? 'Selected for price entry'
              : 'Select for price entry'}
          </Button>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Price history
          </Typography>
          {(prices[unitClass.externalId] ?? []).length === 0 && (
            <Typography>No prices yet.</Typography>
          )}
          {(prices[unitClass.externalId] ?? []).map((price) => (
            <Paper key={price.externalId} variant="outlined" sx={{ p: 1, mb: 1 }}>
              {price.effectiveDate} · version {price.version} · entry ${price.entry} · NAV $
              {price.mid}
              {' · '}exit ${price.exit} · {price.source}
              {price.current ? ' · current' : ''}
            </Paper>
          ))}
        </SectionCard>
      ))}
    </>
  );
}

function PriceEntry({
  draft,
  busy,
  change,
  add,
  importCsv,
}: {
  draft: typeof empty;
  busy: boolean;
  change: (key: keyof typeof empty, value: string) => void;
  add: () => void;
  importCsv: (file: File) => void;
}) {
  return (
    <SectionCard
      title="Add a price"
      subtitle="A correction adds a new version; prior prices remain in history."
    >
      <Box
        sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' } }}
      >
        {(['effectiveDate', 'entry', 'mid', 'exit'] as const).map((key) => (
          <TextField
            key={key}
            label={key === 'effectiveDate' ? 'Effective date' : key}
            type={key === 'effectiveDate' ? 'date' : 'text'}
            value={draft[key]}
            onChange={(event) => change(key, event.target.value)}
            InputLabelProps={key === 'effectiveDate' ? { shrink: true } : undefined}
          />
        ))}
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={busy || Object.values(draft).some((value) => !value)}
          onClick={add}
        >
          Add price
        </Button>
        <Button variant="outlined" component="label" disabled={busy}>
          Import CSV
          <input
            hidden
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) importCsv(file);
            }}
          />
        </Button>
      </Stack>
    </SectionCard>
  );
}

function useFundDetail(fundId: string) {
  const session = useRegistrySession()!;
  const [fund, setFund] = useState<RegistryFund | null>(null);
  const [prices, setPrices] = useState<Record<string, RegistryUnitPrice[]>>({});
  const [draft, setDraft] = useState(empty);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeClass, setActiveClass] = useState('');

  useEffect(() => {
    session.api
      .getFund(fundId)
      .then(async (loaded) => {
        setFund(loaded);
        setActiveClass(loaded.classes[0]?.externalId ?? '');
        const entries = await Promise.all(
          loaded.classes.map(
            async (item) =>
              [item.externalId, await session.api.listPrices(item.externalId)] as const,
          ),
        );
        setPrices(Object.fromEntries(entries));
      })
      .catch((failure) => setError(errorMessage(failure)));
  }, [session, fundId]);

  const add = async () => {
    setBusy(true);
    setError('');
    try {
      const history = await session.api.addPrice(activeClass, draft);
      setPrices((current) => ({ ...current, [activeClass]: history }));
      setDraft(empty);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const importCsv = async (file: File) => {
    setBusy(true);
    setError('');
    try {
      const history = await session.api.importPrices(activeClass, await file.text());
      setPrices((current) => ({ ...current, [activeClass]: history }));
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  return {
    session,
    fund,
    prices,
    draft,
    error,
    busy,
    activeClass,
    setActiveClass,
    setDraft,
    add,
    importCsv,
  };
}

export default function LiveFundDetailPage() {
  const { fundId = '' } = useParams();
  const {
    session,
    fund,
    prices,
    draft,
    error,
    busy,
    activeClass,
    setActiveClass,
    setDraft,
    add,
    importCsv,
  } = useFundDetail(fundId);
  if (!fund)
    return error ? <Alert severity="error">{error}</Alert> : <Typography>Loading fund…</Typography>;
  return (
    <>
      <PageHeader
        eyebrow="Live from Fides"
        title={fund.legalName}
        subtitle={`${fund.code} · ${fund.fundOpportunity.name}`}
        actions={<StatusChip status={fund.status} />}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Button
        component={RouterLink}
        to={`/manager/setup/${fund.dealSetupExternalId}`}
        sx={{ mb: 2 }}
      >
        Open deal setup
      </Button>
      <Stack spacing={2}>
        <FundClasses
          fund={fund}
          prices={prices}
          activeClass={activeClass}
          select={setActiveClass}
        />
        {activeClass && hasRegistryPermission(session, 'RegistrySetup') && (
          <PriceEntry
            draft={draft}
            busy={busy}
            change={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
            add={add}
            importCsv={importCsv}
          />
        )}
      </Stack>
    </>
  );
}
